import React, { useState, useMemo } from 'react';
// Context Hook'ları
import { useData } from '../context/DataContext';
import { useLanguage } from '../context/LanguageContext';

import { Package, Search, Check, Filter, Trophy } from 'lucide-react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

// --- TÜRKÇE ÇEVİRİ SÖZLÜĞÜ (MANUEL EKLENDİ) ---
// Kappa eşyalarının İngilizce isimlerine karşılık gelen Türkçe çeviriler.
const TURKISH_ITEM_NAMES = {
  "Old firesteel": "Eski çakmak taşı",
  "Antique axe": "Antika balta",
  "Battered antique book": "Yıpranmış antika kitap",
  "FireKlean gun lube": "FireKlean silah yağı",
  "Golden rooster": "Altın horoz",
  "Silver Badge": "Gümüş Rozet",
  "Deadlyslob's beard oil": "Deadlyslob'un sakal yağı",
  "Golden 1GPhone smartphone": "Altın 1GPhone telefon",
  "Jar of DevilDog mayo": "DevilDog mayonez kavanozu",
  "Can of sprats": "Çaça balığı konservesi",
  "Fake mustache": "Takma bıyık",
  "Kotton beanie": "Kotton bere",
  "Can of Dr. Lupo's coffee beans": "Dr. Lupo'nun kahve çekirdekleri",
  "Pestily plague mask": "Pestily veba maskesi",
  "Raven figurine": "Kuzgun heykelciği",
  "Shroud half-mask": "Shroud yarım maske",
  "Veritas guitar pick": "Veritas gitar penası",
  "42 Signature Blend English Tea": "42. İmza Karışımı İngiliz Çayı",
  "Smoke balaclava": "Smoke kar maskesi",
  "Evasion armband": "Evasion kol bandı",
  "Can of RatCola soda": "RatCola teneke kutu",
  "Loot Lord plushie": "Loot Lord peluş oyuncağı",
  "WZ Wallet": "WZ Cüzdanı",
  "LVNDMARK's rat poison": "LVNDMARK'ın fare zehiri",
  "Missam forklift key": "Missam forklift anahtarı",
  "Video cassette with the Cyborg Killer movie": "Cyborg Killer filmi kaseti",
  "BakeEzy cook book": "BakeEzy yemek kitabı",
  "JohnB Liquid DNB glasses": "JohnB Liquid DNB gözlüğü",
  "Glorious E lightweight armored mask": "Glorious E hafif zırhlı maske",
  "Baddie's red beard": "Baddie'nin kızıl sakalı",
  "DRD body armor": "DRD vücut zırhı",
  "Gingy keychain": "Gingy anahtarlık",
  "Golden egg": "Altın yumurta",
  "Press pass (issued for NoiceGuy)": "Basın kartı (NoiceGuy)",
  "Axel parrot figurine": "Axel papağan heykelciği",
  "BEAR Buddy plush toy": "BEAR Buddy peluş oyuncak",
  "Inseq gas pipe wrench": "Inseq gaz borusu anahtarı",
  "Viibiin sneaker": "Viibiin spor ayakkabı",
  "Tamatthi kunai knife replica": "Tamatthi kunai bıçağı replikası",
  "Far-forward GPS Signal Amplifier Unit": "İleri GPS Sinyal Güçlendirici",
  "Advanced current converter": "Gelişmiş akım dönüştürücü",
  "Microcontroller board": "Mikrodenetleyici kartı"
};

function KappaItems({ profile, onUpdate }) {
  // --- CONTEXT VERİLERİ ---
  const { kappaItems: items, isLoaded } = useData();
  const { t, language } = useLanguage(); // language bilgisini de aldık

  const [collectedItems, setCollectedItems] = useState(profile.kappaItems || []);
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'needed', 'collected'
  const [searchTerm, setSearchTerm] = useState('');

  // --- HANDLERS ---
  const toggleItem = (itemId) => {
    const newCollected = collectedItems.includes(itemId)
      ? collectedItems.filter(id => id !== itemId)
      : [...collectedItems, itemId];

    setCollectedItems(newCollected);

    const updatedProfile = { ...profile, kappaItems: newCollected };
    localStorage.setItem('tarkovProfile', JSON.stringify(updatedProfile));
    onUpdate(updatedProfile);
  };

  // --- FİLTRELEME VE ÇEVİRİ ---
  const filteredItems = useMemo(() => {
    if (!items) return [];

    return items.filter(item => {
      // Çeviri Mantığı:
      let displayName = item.name;
      if (language === 'tr') {
        displayName = TURKISH_ITEM_NAMES[item.name] || item.name;
      }

      // ARAMA:
      const matchesSearch = displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.shortName && item.shortName.toLowerCase().includes(searchTerm.toLowerCase()));

      if (!matchesSearch) return false;

      const isCollected = collectedItems.includes(item.id);
      if (filterMode === 'needed' && isCollected) return false;
      if (filterMode === 'collected' && !isCollected) return false;

      return true;
    });
  }, [items, searchTerm, filterMode, collectedItems, language]);

  // --- RENDER ---
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-emerald-500 font-mono text-xl animate-pulse flex items-center gap-2">
          <div className="w-3 h-3 bg-emerald-500 rounded-full animate-bounce"></div>
          {t('loading_collector')}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12 bg-transparent">
      <div className="max-w-[1600px] mx-auto px-6 py-8">

        {/* --- HEADER SECTION --- */}
        <div className="bg-[#09090b] border border-white/5 rounded-sm p-6 mb-8 relative overflow-hidden shadow-lg">
          {/* Subtle Green Glow/Accent */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>

          <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#0c0c0e] rounded-sm flex items-center justify-center border border-white/10 shadow-inner">
                <Trophy className="w-6 h-6 text-emerald-500" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white uppercase tracking-wider">{t('collector_title')}</h1>
                <p className="text-sm text-zinc-400 font-mono">{t('collector_desc')}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#0c0c0e] px-5 py-2.5 rounded-sm border border-white/5">
              <span className="text-3xl font-mono font-bold text-white">{collectedItems.length}</span>
              <span className="text-zinc-600 text-xl font-mono">/</span>
              <span className="text-zinc-500 text-xl font-mono">{items.length}</span>
            </div>
          </div>
        </div>

        {/* --- CONTROLS SECTION --- */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex bg-[#09090b] p-1 rounded-sm border border-white/10 w-full md:w-auto">
            <button
              onClick={() => setFilterMode('all')}
              className={`flex-1 md:flex-none px-6 py-2 rounded-sm text-xs font-bold uppercase tracking-wide transition-all ${filterMode === 'all' ? 'bg-zinc-800 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5'}`}
            >
              {t('all_items')}
            </button>
            <button
              onClick={() => setFilterMode('needed')}
              className={`flex-1 md:flex-none px-6 py-2 rounded-sm text-xs font-bold uppercase tracking-wide transition-all flex items-center justify-center gap-2 ${filterMode === 'needed' ? 'bg-zinc-800 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5'}`}
            >
              <Filter className="w-3 h-3" /> {t('needed')}
            </button>
            <button
              onClick={() => setFilterMode('collected')}
              className={`flex-1 md:flex-none px-6 py-2 rounded-sm text-xs font-bold uppercase tracking-wide transition-all flex items-center justify-center gap-2 ${filterMode === 'collected' ? 'bg-emerald-900/40 text-emerald-400 border border-emerald-500/20 shadow-sm' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5'}`}
            >
              <Check className="w-3 h-3" /> {t('collected')}
            </button>
          </div>

          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              placeholder={t('search_kappa_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#09090b] border border-white/10 text-zinc-200 text-sm font-mono rounded-sm pl-10 pr-4 py-2.5 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 transition-all placeholder-zinc-600"
            />
          </div>
        </div>

        {/* --- ITEMS GRID --- */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {filteredItems.map(item => {
            const isCollected = collectedItems.includes(item.id);

            // İSİM BELİRLEME
            let displayName = item.name;
            if (language === 'tr') {
              displayName = TURKISH_ITEM_NAMES[item.name] || item.name;
            }

            return (
              <div
                key={item.id}
                onClick={() => toggleItem(item.id)}
                className={`group relative flex flex-col items-center p-5 rounded-sm border transition-all duration-200 cursor-pointer overflow-hidden ${isCollected
                  ? 'bg-[#0c0c0e] border-emerald-500/40 shadow-[0_0_15px_rgba(16,185,129,0.1)]'
                  : 'bg-[#09090b] border-white/5 hover:border-white/20 hover:bg-[#0c0c0e]'
                  }`}
              >
                {/* Tactical Corner Accent (Only when collected) */}
                {isCollected && (
                  <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-emerald-500"></div>
                )}

                {/* Status Indicator (Dot instead of Check icon for cleaner look, or small check) */}
                <div className="absolute top-3 right-3">
                  <div className={`w-2 h-2 rounded-full transition-all duration-300 ${isCollected ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]' : 'bg-zinc-800'
                    }`}></div>
                </div>

                {/* Item Image */}
                <div className="w-24 h-24 flex items-center justify-center mb-4 relative">
                  {/* Background Glow */}
                  <div className={`absolute inset-0 bg-emerald-500/5 blur-2xl rounded-full transition-opacity duration-300 ${isCollected ? 'opacity-100' : 'opacity-0 group-hover:opacity-20'}`}></div>

                  <LazyLoadImage
                    src={item.iconLink}
                    alt={item.name}
                    effect="blur"
                    className={`relative w-full h-full object-contain transition-all duration-300 ${isCollected ? 'scale-110 drop-shadow-[0_0_15px_rgba(16,185,129,0.2)]' : 'opacity-60 grayscale group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105'}`}
                  />
                </div>

                {/* Item Name */}
                <div className="text-center w-full relative z-10">
                  <span className={`text-[11px] font-bold uppercase tracking-wide leading-tight block line-clamp-2 ${isCollected ? 'text-emerald-400' : 'text-zinc-500 group-hover:text-zinc-300'}`} title={item.name}>
                    {displayName}
                  </span>
                </div>

                {/* Bottom Active Bar */}
                <div className={`absolute bottom-0 left-0 h-0.5 transition-all duration-300 ${isCollected ? 'w-full bg-emerald-500' : 'w-0 bg-zinc-700 group-hover:w-1/2'}`}></div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredItems.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-zinc-600">
            <Package className="w-16 h-16 mb-4 opacity-10" strokeWidth={1} />
            <p className="font-mono text-sm uppercase tracking-widest">{t('no_items_found')}</p>
          </div>
        )}

      </div>
    </div>
  );
}

export default KappaItems;